import React, { useState, useRef } from 'react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { conf } from '../domain/services/configService';

const isServerSide = typeof window === 'undefined';

export const WithLazyHydration =
  (Component, opts = {}) =>
  ({ ...props }) => {
    const defaultOptions = { name: null, minHeight: 1000 };
    const options = { ...defaultOptions, ...opts };
    const [isHydrated, setIsHydrated] = useState(false);
    const wrapperRef = useRef(null);
    // eslint-disable-next-line react/prop-types
    const name = props.data?.type || props.type || options.name;
    useIntersectionObserver(
      wrapperRef,
      async () => {
        // If "Component" is lazy-loadable, load it before hydrating it.
        if (Component.load) {
          await Component.load();
        }
        setIsHydrated(true);
      },
      {
        init: {
          root: null,
          rootMargin: '500px 0px 500px 0px',
          threshold: 0.0
        },
        disconnect: true,
        // eslint-disable-next-line react/prop-types
        preflightConditionForTriggeringCallback: () => !!props?.isAboveTheFold
      }
    );

    if (isHydrated || isServerSide) {
      return <Component {...props} />;
    }

    // Only set a minimum height of 1000px when doing SPA navigation:
    // 1) when we are on preview (first render and subsequent ones need that height)
    // 2) when we are on a subsequent render on production (first render does not
    // need that height, but subsequent ones do)
    const styles =
      !isServerSide &&
      // eslint-disable-next-line no-underscore-dangle
      (conf.isPreview || window.___gatsbyInitialClientRenderDone)
        ? { minHeight: options.minHeight }
        : null;

    return (
      <div
        ref={wrapperRef}
        data-with-lazy-hydration-name={name}
        style={styles}
        // Setting InnerHTML this way leads to React attaching
        // the ref and the rest of the props to the "div" element,
        // but not deleting its children, hence skipping hydration.
        // This is the standard way in react to skip hydration and it
        // is used by several libraries.
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: '' }}
        suppressHydrationWarning
      />
    );
  };
